import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/** What the user picked. `system` follows the operating system. */
export type ThemePreference = 'dark' | 'light' | 'system';
/** What is actually applied. */
export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'hearth.theme';
const QUERY = '(prefers-color-scheme: dark)';

interface ThemeContextValue {
  /** The applied theme, for components that need to know (Monaco, toasts). */
  theme: Theme;
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'system') return stored;
  } catch {
    // Storage can be unavailable (private mode): fall back to the default.
  }
  return 'dark';
}

const resolve = (preference: ThemePreference): Theme =>
  preference === 'system' ? (matchMedia(QUERY).matches ? 'dark' : 'light') : preference;

function apply(theme: Theme, animate: boolean) {
  const root = document.documentElement;
  if (animate) {
    // Fade colours only while switching, so ordinary hover states aren't slowed down.
    root.classList.add('theme-switching');
    window.setTimeout(() => root.classList.remove('theme-switching'), 250);
  }
  root.classList.toggle('dark', theme === 'dark');
}

/** index.html applies the saved theme before paint; this keeps React in sync with it. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);
  const [theme, setTheme] = useState<Theme>(() =>
    document.documentElement.classList.contains('dark') ? 'dark' : 'light',
  );

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // The choice still applies for this session.
    }
    const resolved = resolve(next);
    apply(resolved, true);
    setTheme(resolved);
  }, []);

  // While on "system", follow the OS when it changes.
  useEffect(() => {
    if (preference !== 'system') return;
    const media = matchMedia(QUERY);
    const onChange = () => {
      const resolved = resolve('system');
      apply(resolved, true);
      setTheme(resolved);
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [preference]);

  // Keep several open tabs in step.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const next = readPreference();
      setPreferenceState(next);
      const resolved = resolve(next);
      apply(resolved, true);
      setTheme(resolved);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo(
    () => ({ theme, preference, setPreference }),
    [theme, preference, setPreference],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
